import React, { useEffect, useRef, useState } from "react";
import { ErrorMessage, FieldArray, Form, Formik, FormikHelpers } from "formik";
import { Button, Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ApolloError, useApolloClient, useReactiveVar } from "@apollo/client";
import { useMatch, useNavigate, useParams } from "react-router-dom";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import TextInputWithCounter from "../inputs/TextInputWithCounter";
import {
    CustomModal,
    PriceInput,
    ReactSelect,
    Spinner,
    SpinnerLoader,
    TextInput,
    TooltipCustom,
} from "@services/ui-components";
import TextareaInput from "../inputs/TextareaInput";
import CriticalResidueInput from "../inputs/CriticalResidueInput";
import {
    buildInitialAltGroup,
    clearAltErrors,
    createProductFormValidationSchema,
    getAutoAltUnitId,
    getMainUnitOptions,
    getUnitLabelByValue,
    getUnitNameById,
    ALT_UNIT_GROUP_NONE_ID,
    unitGroupQuantityId,
    unitPackId,
    unitPCId,
    getMainUnitGroup,
} from "../constants";
import ErrorService from "../../../services/ErrorService";
import { CategoryInterface, DuplicateProductData, unitGroupI } from "../types";
import NumberInput from "../inputs/NumberInput";
import { ServiceModalWrapper } from "@me-pos/service-modal";
import TextInputWithButton from "../inputs/TextInputWithButton";
import ImageCrop from "@services/ui-components/src/ImageCrop/ImageCrop";
import CategoryProduct from "../services/CategoryProduct";
import RequestInputService from "../services/RequestInputService";
import { normalizeCategoryData } from "../services/normalizeCategoryData";
import { refreshProductCategoriesCache } from "../services/refreshProductCategoriesCache";
import TechnologicalMapsList from "./TechnologicalMapsList";
import {
    GetBrandsDocument,
    GetProductsDocument,
    useCreateProductCategoryMutation,
    useGeMainUnitsForCreateUpdateProductQuery,
    useGetAlternativeUnitGroupsForCreateUpdateProductQuery,
    useGetBrandsQuery,
    useGetOneProductQuery,
    useGetProductCategoriesQuery,
    useGetUnitsQuery,
    useRestoreProductMutation,
    useUpdateProductMutation,
} from "@me-team/host/main/products/graphql/products.hooks";
import {
    CompanyAccess,
    ProductAttributesInput,
    ProductCategoryNewInput,
    ProductCategoryNewProxy,
    ProductInput,
    ProductParametersProxy,
    ProductProxy,
    UnitProxy,
} from "@me-team/host/main/graphql/types";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import UpdateProductFormEffects from "./UpdateProductFormEffects";
import { DeepPartial } from "@apollo/client/utilities";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

interface ValidationError {
    [key: string]: { message: string; code: string | null }[];
}
export interface Option {
    value: number;
    label: string;
}

const UpdateRecoveryProductForm: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const client = useApolloClient();
    const { id, branchId } = useParams();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const canEditProduct = useRoleAccess(CompanyAccess.ProductEdit);
    const canDeleteRestoreProduct = useRoleAccess(CompanyAccess.ProductDeleteRestore);
    const canCreateProductCategory = useRoleAccess(CompanyAccess.ProductCategoryCreate);
    const buttonCategoryRef = useRef<HTMLButtonElement>(null);
    const isEdit = useMatch(`/warehouse/product/${currentCompanyId}/${id}/edit-product/branch/${branchId}`);
    const canSubmit = isEdit ? canEditProduct : canDeleteRestoreProduct;
    const isProductFormReadOnly = !canSubmit;
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const [isProductModalOpen, setIsProductModalOpen] = useState<boolean>(false);
    const [currentCategory, setCurrentCategory] = useState(null);
    const [categoryList, setCategoryList] = useState<CategoryInterface[]>(null);
    const [categoryInput, setCategoryInput] = useState(null);
    const [selectedMainUnitId, setSelectedMainUnitId] = useState<number>(null);
    const [relationsAlternativeUnits, setRelationsAlternativeUnits] = useState<UnitProxy[]>(null);

    const [pickedCategoryId, setPickedCategoryId] = useState<number>(null);
    const [duplicateProductData, setDuplicateProductData] = useState<DuplicateProductData | null>(null);
    const [isDuplicateProduct, setIsDuplicateProduct] = useState<boolean>(false);

    const [createProductCategory] = useCreateProductCategoryMutation();
    const [updateProduct] = useUpdateProductMutation();
    const [restoreProduct] = useRestoreProductMutation();

    const {
        data: productOneData,
        loading: productDataLoading,
        refetch: refetchProduct,
    } = useGetOneProductQuery({
        skip: !currentCompanyId,
        variables: {
            id: +id,
            companyId: Number(currentCompanyId),
            branchId: Number(branchId),
            needsFilterByBranch: false,
        },
        context: {
            errorType: "global",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });
    const product: DeepPartial<ProductProxy> = productOneData?.products?.items[0];

    const startValueCategory = product?.productCategoryNew?.parentCategory?.id
        ? product?.productCategoryNew?.parentCategory?.id
        : product?.productCategoryNew?.id;

    const startValueSubCategory = product?.productCategoryNew?.parentCategory?.id
        ? product?.productCategoryNew?.id
        : null;

    const categoryName = product?.productCategoryNew?.parentCategory?.name
        ? product?.productCategoryNew?.parentCategory?.name
        : product?.productCategoryNew?.name;

    const subCategoryName = product?.productCategoryNew?.parentCategory?.name
        ? product?.productCategoryNew?.name
        : null;

    const [categoryText, setCategoryText] = useState([categoryName, subCategoryName]);

    const [isArchiveCategory, setIsArchiveCategory] = useState<boolean>(product?.productCategoryNew?.deleted);
    useEffect(() => {
        if (categoryName || subCategoryName) {
            setCategoryText([categoryName ?? "", subCategoryName ?? ""]);
            setIsArchiveCategory(product?.productCategoryNew?.deleted);
        }
    }, [categoryName, subCategoryName, product?.productCategoryNew?.deleted]);

    const {
        data: productCategoriesData,
        refetch: refetchCategories,
        loading: productCategoryLoading,
    } = useGetProductCategoriesQuery({
        variables: {
            itemsPerPage: 1000,
            branchId: Number(branchId),
        },
        context: {
            errorType: "local",
        },
        onCompleted: (data) => {
            const normalizedItems = normalizeCategoryData(data);
            setCategoryList(
                CategoryProduct.categoryModuleCreator(normalizedItems, startValueCategory, startValueSubCategory)
            );
            setCategoryInput(
                RequestInputService.createServiceCategoryInput(
                    startValueCategory,
                    startValueSubCategory,
                    normalizedItems
                )
            );
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const categoryListData: ProductCategoryNewProxy[] = productCategoriesData?.productCategories?.items;

    const { data: brandsData, loading: brandsLoading } = useGetBrandsQuery({
        variables: {
            companyId: +currentCompanyId,
            branchId: Number(branchId),
        },
        skip: !currentCompanyId,
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const suggestionsList: ProductParametersProxy[] = brandsData?.companyBrands;

    const handleSubmitCreateCategory = async (input: ProductCategoryNewInput) => {
        if (!canCreateProductCategory) return;

        try {
            currentCompanyId &&
                (await createProductCategory({
                    variables: {
                        companyId: +currentCompanyId,
                        input,
                        branchId: Number(branchId),
                    },
                    context: {
                        errorType: "local",
                    },
                    onCompleted: async (data) => {
                        data && setCurrentCategory(data.createProductCategoryNew);
                        await refreshProductCategoriesCache(client, branchId);
                    },
                    onError: (error) => {
                        setIsErrorModalOpen(ErrorService.errorHandling(error));
                    },
                }));
        } catch (error) {
            console.error(error);
        }
    };

    const handleOpenDuplicateOfProductModal = () => {
        setIsDuplicateProduct(true);
    };
    const handleCloseDuplicateOfProductModal = () => {
        setIsDuplicateProduct(false);
    };

    const handleLabelClick = (event: React.MouseEvent<HTMLLabelElement>) => {
        event.preventDefault();
        if (buttonCategoryRef.current) {
            buttonCategoryRef.current.focus();
        }
    };

    const handleCategoryNameChange = (categoryName: string) => {
        setCategoryText([categoryName]);
    };

    const handleSubCategoryNameChange = (subCategoryName: string, categoryName: string) => {
        setCategoryText([categoryName, subCategoryName]);
    };

    const { data: mainUnitsData, loading: mainUnitsLoading } = useGeMainUnitsForCreateUpdateProductQuery({
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });
    const { data: alternativeUnitGroupsData, refetch: alternativeUnitGroupsRefetch } =
        useGetAlternativeUnitGroupsForCreateUpdateProductQuery({
            variables: { mainUnitId: selectedMainUnitId },
            skip: !selectedMainUnitId,
            context: {
                errorType: "local",
            },
            onError: (error: ApolloError) => {
                setIsErrorModalOpen(ErrorService.errorHandling(error));
            },
        });
    const { data: unitsData } = useGetUnitsQuery({
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const handleMainUnit = async (
        selectedOption: Option,
        values: ProductInput,
        setFieldValue: FormikHelpers<ProductInput>["setFieldValue"]
    ) => {
        const prevMainUnitId = values.unit ?? null;
        const prevAltGroupId = values.alternativeUnitGroupInput?.alternativeUnitGroup ?? null;

        const nextMainUnitId: number = selectedOption.value;
        setFieldValue("unit", nextMainUnitId);

        if (prevAltGroupId === ALT_UNIT_GROUP_NONE_ID && prevMainUnitId !== unitPCId) {
            const autoUnitId = getAutoAltUnitId(nextMainUnitId);
            setFieldValue("alternativeUnitGroupInput.alternativeUnitGroup", ALT_UNIT_GROUP_NONE_ID, false);
            setFieldValue("alternativeUnitGroupInput.alternativeUnit", autoUnitId, false);
            setFieldValue("alternativeUnitGroupInput.alternativeValue", 1, false);
            await alternativeUnitGroupsRefetch({ mainUnitId: nextMainUnitId });
            return;
        }

        if (prevMainUnitId === unitPackId && prevAltGroupId === unitGroupQuantityId && nextMainUnitId === unitPCId) {
            const autoUnitId = getAutoAltUnitId(nextMainUnitId);
            setFieldValue("alternativeUnitGroupInput.alternativeUnitGroup", ALT_UNIT_GROUP_NONE_ID, false);
            setFieldValue("alternativeUnitGroupInput.alternativeUnit", autoUnitId, false);
            setFieldValue("alternativeUnitGroupInput.alternativeValue", 1, false);
            await alternativeUnitGroupsRefetch({ mainUnitId: nextMainUnitId });
            return;
        }

        const initialMainUnitId = initialValues.unit ?? null;

        if (nextMainUnitId === initialMainUnitId) {
            await alternativeUnitGroupsRefetch({ mainUnitId: nextMainUnitId });
            setFieldValue(
                "alternativeUnitGroupInput.alternativeUnitGroup",
                initialValues.alternativeUnitGroupInput?.alternativeUnitGroup ?? null,
                false
            );
            setFieldValue(
                "alternativeUnitGroupInput.alternativeUnit",
                initialValues.alternativeUnitGroupInput?.alternativeUnit ?? null,
                false
            );
            setFieldValue(
                "alternativeUnitGroupInput.alternativeValue",
                initialValues.alternativeUnitGroupInput?.alternativeValue ?? null,
                false
            );
            return;
        }

        await alternativeUnitGroupsRefetch({ mainUnitId: nextMainUnitId });
    };

    const unitOptions: Option[] = getMainUnitOptions(mainUnitsData?.relations?.mainUnits);

    const alternativeUnitGroupOptions: Option[] = [
        ...((alternativeUnitGroupsData?.relations?.alternativeUnitGroups ?? []).map((unitGroup: unitGroupI) => ({
            value: unitGroup.id,
            label: unitGroup.name,
        })) as Option[]),
        { value: ALT_UNIT_GROUP_NONE_ID, label: t("none") },
    ];

    const handleAlternativeUnitGroupChange = (
        selectedOption: Option,
        setFieldValue: FormikHelpers<ProductInput>["setFieldValue"],
        setFieldTouched: FormikHelpers<ProductInput>["setFieldTouched"],
        setFieldError: FormikHelpers<ProductInput>["setFieldError"],
        mainUnitId: number | null
    ) => {
        const nextGroupId =
            selectedOption?.value !== undefined && selectedOption?.value !== null ? Number(selectedOption.value) : null;

        setFieldValue("alternativeUnitGroupInput.alternativeUnitGroup", nextGroupId, false);

        clearAltErrors(setFieldError);

        if (nextGroupId === ALT_UNIT_GROUP_NONE_ID) {
            const autoUnitId = getAutoAltUnitId(mainUnitId);
            setRelationsAlternativeUnits([]);

            setFieldValue("alternativeUnitGroupInput.alternativeUnit", autoUnitId, false);
            setFieldValue("alternativeUnitGroupInput.alternativeValue", 1, false);
            setFieldTouched("alternativeUnitGroupInput.alternativeUnitGroup", false, false);
            setFieldTouched("alternativeUnitGroupInput.alternativeUnit", false, false);
            setFieldTouched("alternativeUnitGroupInput.alternativeValue", false, false);

            return;
        }

        const filteredAlternativeUnits =
            unitsData?.relations?.units?.filter((unit) => unit.unitGroup.id === nextGroupId) || [];

        setRelationsAlternativeUnits(filteredAlternativeUnits);

        if (filteredAlternativeUnits.length > 0) {
            setFieldValue("alternativeUnitGroupInput.alternativeUnit", filteredAlternativeUnits[0].id, false);
        } else {
            setFieldValue("alternativeUnitGroupInput.alternativeUnit", null, false);
        }

        setFieldValue("alternativeUnitGroupInput.alternativeValue", null, false);

        setFieldError("alternativeUnitGroupInput.alternativeUnitGroup", undefined);
        setFieldError("alternativeUnitGroupInput.alternativeUnit", undefined);
        setFieldError("alternativeUnitGroupInput.alternativeValue", undefined);
    };

    const handleAlternativeUnitChange = (
        selectedOption: Option,
        setFieldValue: FormikHelpers<ProductInput>["setFieldValue"]
    ) => {
        setFieldValue("alternativeUnitGroupInput.alternativeUnit", selectedOption.value);
    };

    const handleCancel = () => navigate(`/warehouse/product/${currentCompanyId}/branch/${branchId}`);

    const productAttributes: ProductAttributesInput[] = product?.productAttributes?.length
        ? product.productAttributes.map((attr: any) => ({
              name: attr.name,
              property: attr.value,
          }))
        : [];

    const mainUnitIdFromProduct = product?.unit?.id ?? null;

    const initialValues: ProductInput = {
        logo: product?.image ? product?.image : null,
        name: product?.name,
        brand: product?.brand,
        category: product?.productCategoryNew?.id && product?.productCategoryNew?.id,
        sku: product?.sku ? product?.sku : "",
        barcode: product?.barcode ? product?.barcode : "",
        description: product?.description ? product?.description : "",
        unitGroup: product?.unitGroup?.id && product?.unitGroup?.id,
        unit: product?.unit?.id && product?.unit?.id,
        criticalResidue: product?.criticalResidue,
        alternativeUnitGroupInput: buildInitialAltGroup(product, mainUnitIdFromProduct),
        productAttributes: productAttributes,
        cost: product?.cost ? Number(product.cost) : null,
        price: product?.price ? Number(product.price) : null,
    };
    const handleUpdateSubmit = async (values: ProductInput) => {
        if (!canSubmit) return;

        const isNone = values?.alternativeUnitGroupInput?.alternativeUnitGroup === ALT_UNIT_GROUP_NONE_ID;
        const input: ProductInput = {
            name: values?.name,
            brand: values?.brand,
            category: values?.category,
            sku: values?.sku || null,
            barcode: values?.barcode || null,
            description: values?.description || null,
            unitGroup: getMainUnitGroup(values?.unit),
            unit: values?.unit,
            criticalResidue: values?.criticalResidue || null,
            alternativeUnitGroupInput: isNone
                ? null
                : values?.alternativeUnitGroupInput?.alternativeUnitGroup == null &&
                    values?.alternativeUnitGroupInput?.alternativeUnit == null &&
                    values?.alternativeUnitGroupInput?.alternativeValue == null
                  ? null
                  : {
                        alternativeUnitGroup: values?.alternativeUnitGroupInput?.alternativeUnitGroup,
                        alternativeUnit: values?.alternativeUnitGroupInput?.alternativeUnit,
                        alternativeValue: values?.alternativeUnitGroupInput?.alternativeValue,
                    },
            productAttributes: values?.productAttributes ? values?.productAttributes : null,
            isUpdateLogo: values?.isUpdateLogo || false,
            cost: values?.cost,
            price: values?.price,
        };
        if (values?.isUpdateLogo === true) input.logo = values.logo;
        const mutateProduct = isEdit ? updateProduct : restoreProduct;

        await mutateProduct({
            variables: { productId: +id, input, branchId: Number(branchId) },
            context: {
                errorType: "local",
            },
            refetchQueries: [
                {
                    query: GetBrandsDocument,
                    variables: {
                        companyId: Number(currentCompanyId),
                        branchId: Number(branchId),
                    },
                },
            ],
            onCompleted: async (data) => {
                await refreshProductCategoriesCache(client, branchId);
                data &&
                    (await client.query({
                        query: GetProductsDocument,
                        variables: {
                            page: 1,
                            itemsPerPage: 1000,
                            companyId: Number(currentCompanyId),
                            branchId: Number(branchId),
                        },
                    }));
                await refetchProduct();
                data &&
                    navigate(`/warehouse/product/${currentCompanyId}/branch/${branchId}`, {
                        state: {
                            text: isEdit ? t("Saved") : t("Restored"),
                            toast: true,
                        },
                    });
            },
            onError: (error: ApolloError) => {
                const validationErrors = (error?.graphQLErrors[0]?.extensions as { validation?: ValidationError })
                    ?.validation;

                const extractedData: Record<string, string> = {};

                if (validationErrors && validationErrors[""]) {
                    validationErrors[""]?.forEach((err: any) => {
                        const message: string = err.message;

                        if (message.includes("The product already exists with")) {
                            const regex = /name\s*=\s*"(.+?)"\s*,\s*brand\s*=\s*"(.+?)"\s*and\s*id\s*=\s*"(.+?)"/;
                            const match = message.match(regex);

                            if (match) {
                                const [, name, brand, id] = match;
                                extractedData.name = name;
                                extractedData.brand = brand;
                                extractedData.productId = id;
                                setDuplicateProductData(extractedData);
                                handleOpenDuplicateOfProductModal();
                            }
                        }
                    });
                } else {
                    setIsErrorModalOpen(ErrorService.errorHandling(error));
                }
            },
        });
    };

    const handlePhoto = (setFieldValue: any) => {
        setFieldValue("isUpdateLogo", true);
    };

    const handleNavigateAndClose = () => {
        handleCloseDuplicateOfProductModal();
        navigate(
            `/warehouse/product/${currentCompanyId}/${duplicateProductData?.productId}/edit-product/branch/${branchId}`
        );
    };

    if (brandsLoading || productCategoryLoading || productDataLoading || !currentCompanyId || mainUnitsLoading)
        return <Spinner />;

    return (
        <>
            <Formik
                initialValues={initialValues}
                validationSchema={createProductFormValidationSchema(t, isArchiveCategory)}
                onSubmit={handleUpdateSubmit}
            >
                {({ setFieldValue, setFieldTouched, values, touched, errors, isSubmitting, setFieldError }) => {
                    const selectedGroupId = values?.alternativeUnitGroupInput?.alternativeUnitGroup;

                    const isAltNoneSelected = selectedGroupId === ALT_UNIT_GROUP_NONE_ID;
                    let alternativeUnitOptions: Option[] = [];

                    if (isAltNoneSelected) {
                        const id = getAutoAltUnitId(values.unit);
                        const label = getUnitNameById(id, unitsData?.relations?.units);
                        alternativeUnitOptions = [{ value: id, label }];
                    } else {
                        alternativeUnitOptions =
                            relationsAlternativeUnits?.map((unit: unitGroupI) => {
                                return {
                                    value: unit?.id,
                                    label: unit?.name,
                                };
                            }) || [];
                    }
                    return (
                        <>
                            <UpdateProductFormEffects
                                pickedCategoryId={pickedCategoryId}
                                unitId={values?.unit ?? null}
                                selectedGroupId={selectedGroupId}
                                unitsData={unitsData}
                                setSelectedMainUnitId={setSelectedMainUnitId}
                                setRelationsAlternativeUnits={setRelationsAlternativeUnits}
                            />
                            <Form>
                                <Row className="container-max-widths-xxl flex-column flex-lg-row  mb-5">
                                    <Col lg={9} className="pe-lg-5">
                                        <h4 className="mb-4">{t("Information")}</h4>
                                        <Row className="mb-3 mb-sm-4 fs-7">
                                            <TextInputWithCounter
                                                id="updateProduct-name-field"
                                                label={t("Product Name")}
                                                name="name"
                                                placeholder={t("Enter the Product Name")}
                                                isDisabled={isProductFormReadOnly}
                                            />
                                        </Row>
                                        <Row className="mb-3 mb-sm-4 fs-7">
                                            <TextInputWithCounter
                                                id="updateProduct-brand-field"
                                                label={t("Brand")}
                                                name="brand"
                                                placeholder={t("Enter a Brand")}
                                                suggestions={suggestionsList}
                                                isDisabled={isProductFormReadOnly}
                                            />
                                        </Row>
                                        <Row className="mb-3 mb-sm-4 fs-7">
                                            <TextInput
                                                id="updateProduct-sku-field"
                                                name="sku"
                                                placeholder="Enter a SKU"
                                                label={t("ProductsList.SKU")}
                                                maxLength={50}
                                                disabled={isProductFormReadOnly}
                                            />
                                        </Row>
                                        <Row className="mb-3 mb-sm-4 fs-7">
                                            <TextInput
                                                id="updateProduct-barcode-field"
                                                name="barcode"
                                                placeholder="Enter a Barcode"
                                                label={t("ProductsList.Barcode")}
                                                maxLength={20}
                                                disabled={isProductFormReadOnly}
                                            />
                                        </Row>
                                        <Row className="mb-sm-4">
                                            <Col sm={3} md={2} className="mb-2 mb-md-0">
                                                <label
                                                    htmlFor="updateProduct-category-field"
                                                    className="text-dark fs-7 fw-bold py-1"
                                                    onClick={handleLabelClick}
                                                >
                                                    <span className="required-label">{t("Category")}</span>
                                                </label>
                                            </Col>
                                            <Col sm={9} md={10}>
                                                <div>
                                                    <span
                                                        className={`${categoryText[0] ? "me-4" : "d-none"}  mb-2 mb-lg-0 ${isArchiveCategory ? "text-grey" : "text-dark"} `}
                                                    >
                                                        {categoryText[0]}{" "}
                                                        {categoryText[1] && (
                                                            <>
                                                                <i className="bi bi-arrow-right"></i> {categoryText[1]}
                                                            </>
                                                        )}
                                                        {isArchiveCategory && <span>{t("Archive")}</span>}
                                                    </span>
                                                    <Button
                                                        ref={buttonCategoryRef}
                                                        variant={`${touched.category && errors.category ? "outline-danger" : "outline-primary"}`}
                                                        className="w-100 fw-normal py-1"
                                                        id="updateService-category-field"
                                                        style={{ maxWidth: "149px" }}
                                                        onClick={() => {
                                                            setIsProductModalOpen(true);
                                                        }}
                                                        disabled={isProductFormReadOnly}
                                                    >
                                                        {categoryText[0] ? t("Change Category") : t("Select Category")}
                                                    </Button>
                                                    <ErrorMessage name="category">
                                                        {(msg) => <div className="text-danger fs-7 mt-1">{msg}</div>}
                                                    </ErrorMessage>
                                                </div>
                                            </Col>
                                        </Row>
                                        <Row className="mb-3 mb-sm-4 fs-7">
                                            <TextareaInput
                                                label={t("Description")}
                                                name="description"
                                                id="updateProduct-description-field"
                                                placeholder={t("Enter a Description")}
                                                isDisabled={isProductFormReadOnly}
                                            />
                                        </Row>
                                        <Row className="mb-3 mb-sm-4 fs-7">
                                            <CriticalResidueInput
                                                label={t("Critical Residue")}
                                                name="criticalResidue"
                                                id="updateProduct-criticalResidue-field"
                                                placeholder={t("Enter a Critical Residue")}
                                                textTooltip={t(
                                                    "The number of products in stock, upon reaching which a warning email will be sent."
                                                )}
                                                isDisabled={isProductFormReadOnly}
                                                maxLength={9}
                                            />
                                        </Row>
                                        <Row className="mb-3 mb-sm-4">
                                            <Col sm={3} md={2} className="mb-2 mb-sm-0 d-flex">
                                                <label
                                                    htmlFor="updateProduct-unit-field"
                                                    className="text-dark fs-7 fw-bold"
                                                >
                                                    <span className="required-label">
                                                        {t("Unit of Measure for sale")}
                                                    </span>
                                                </label>
                                            </Col>
                                            <Col sm={9} md={10}>
                                                <Row className="gap-4 gap-xxl-0">
                                                    <Col xxl={2}>
                                                        <ReactSelect
                                                            id="updateProduct-unit-field"
                                                            name="unit"
                                                            placeholder={t("Select an unit")}
                                                            options={unitOptions}
                                                            value={unitOptions?.find(
                                                                (option: Option) => option.value === values.unit
                                                            )}
                                                            onChange={(selectedOption: Option) =>
                                                                handleMainUnit(selectedOption, values, setFieldValue)
                                                            }
                                                            isSearchable={false}
                                                            disabled={isProductFormReadOnly}
                                                        />
                                                        <ErrorMessage name="unit">
                                                            {(msg) => (
                                                                <div className="invalid-feedback d-block">{msg}</div>
                                                            )}
                                                        </ErrorMessage>
                                                    </Col>
                                                    <Col xxl={5} className="mb-2 mb-md-0 d-flex">
                                                        <Col
                                                            xs={6}
                                                            sm={5}
                                                            xxl={6}
                                                            className="d-flex justify-content-xxl-end"
                                                        >
                                                            <label
                                                                htmlFor="updateProduct-cost-field"
                                                                className="text-dark fs-7 fw-bold me-3 h-100 d-flex align-items-center"
                                                                style={{ maxHeight: "49px" }}
                                                            >
                                                                <span>
                                                                    {t("Cost price 1 {{unit}}", {
                                                                        unit: t(
                                                                            getUnitLabelByValue(
                                                                                unitOptions,
                                                                                values?.unit,
                                                                                "costPrice"
                                                                            )
                                                                        ),
                                                                    })}
                                                                </span>
                                                            </label>
                                                        </Col>
                                                        <Col xs={6} sm={7} xxl={6}>
                                                            <PriceInput
                                                                name="cost"
                                                                placeholder="0.00"
                                                                initialValue={initialValues.cost}
                                                                id="updateProduct-cost-field"
                                                                disabled={isProductFormReadOnly}
                                                            />
                                                        </Col>
                                                    </Col>
                                                    <Col xxl={5} className="mb-2 mb-md-0 d-flex">
                                                        <Col
                                                            xs={6}
                                                            sm={5}
                                                            xxl={6}
                                                            className="d-flex justify-content-xxl-end"
                                                        >
                                                            <label
                                                                htmlFor="updateProduct-price-field"
                                                                className="text-dark fs-7 fw-bold me-3 h-100 d-flex align-items-center"
                                                                style={{ maxHeight: "49px" }}
                                                            >
                                                                <span>
                                                                    {t("Selling price")}&nbsp;
                                                                    <span className="text-danger">*</span>
                                                                    <br />
                                                                    {t(
                                                                        getUnitLabelByValue(
                                                                            unitOptions,
                                                                            values?.unit,
                                                                            "sellingPrice"
                                                                        )
                                                                    )}
                                                                </span>
                                                            </label>
                                                        </Col>
                                                        <Col xs={6} sm={7} xxl={6}>
                                                            <PriceInput
                                                                name="price"
                                                                placeholder="0.00"
                                                                initialValue={initialValues.price}
                                                                id="updateProduct-price-field"
                                                                disabled={isProductFormReadOnly}
                                                            />
                                                        </Col>
                                                    </Col>
                                                </Row>
                                            </Col>
                                        </Row>
                                        <Row className="mb-3 mb-sm-4">
                                            <Col sm={3} md={2} className="mb-2 mb-sm-0 d-flex">
                                                <label
                                                    htmlFor="updateProduct-alternativeUnitGroup-field"
                                                    className="text-dark fs-7 fw-bold text-break"
                                                >
                                                    <span className="react-datepicker--time-only">
                                                        {t("Alternative measuring unit")}
                                                    </span>
                                                </label>
                                            </Col>
                                            <Col sm={9} md={10}>
                                                <Row className="g-4">
                                                    <Col xxl={5}>
                                                        <ReactSelect
                                                            id="updateProduct-alternativeUnitGroup-field"
                                                            name="alternativeUnitGroupInput.alternativeUnitGroup"
                                                            placeholder={t("Select type of measure")}
                                                            options={alternativeUnitGroupOptions}
                                                            value={
                                                                selectedGroupId == null
                                                                    ? null
                                                                    : (alternativeUnitGroupOptions?.find(
                                                                          (option) => option.value === selectedGroupId
                                                                      ) ?? null)
                                                            }
                                                            onChange={(selectedOption: Option) =>
                                                                handleAlternativeUnitGroupChange(
                                                                    selectedOption,
                                                                    setFieldValue,
                                                                    setFieldTouched,
                                                                    setFieldError,
                                                                    values?.unit
                                                                )
                                                            }
                                                            isSearchable={false}
                                                            disabled={true}
                                                        />
                                                        <div>
                                                            <span className="fs-7 text-grey">
                                                                {t("for ex. weight")}
                                                            </span>
                                                        </div>
                                                        <ErrorMessage name="alternativeUnitGroupInput.alternativeUnitGroup">
                                                            {(msg) => (
                                                                <div className="invalid-feedback d-block">{msg}</div>
                                                            )}
                                                        </ErrorMessage>
                                                    </Col>
                                                    <Col md={7} xxl={4} className="mb-2 mb-md-0 d-flex">
                                                        <label
                                                            htmlFor="updateProduct-alternativeUnitGroupInput.alternativeValue-field"
                                                            className={`text-dark fs-7 fw-bold h-100 d-flex align-items-center ${getUnitLabelByValue(unitOptions, values?.unit, "quantity") ? "me-3" : ""}`}
                                                            style={{ whiteSpace: "nowrap", maxHeight: "49px" }}
                                                        >
                                                            <span>
                                                                {t(
                                                                    getUnitLabelByValue(
                                                                        unitOptions,
                                                                        values?.unit,
                                                                        "quantity"
                                                                    )
                                                                )}
                                                            </span>
                                                        </label>
                                                        <NumberInput
                                                            id="updateProduct-alternativeUnitGroupInput.alternativeValue-field"
                                                            name="alternativeUnitGroupInput.alternativeValue"
                                                            placeholder={t("Add quantity")}
                                                            maxLength={9}
                                                            disabled={isProductFormReadOnly || isAltNoneSelected}
                                                        />
                                                    </Col>
                                                    <Col md={5} xxl={3} className="mb-2 mb-md-0">
                                                        <ReactSelect
                                                            id="updateProduct-alternativeUnit-field"
                                                            name="alternativeUnitGroupInput.alternativeUnit"
                                                            placeholder={t("Select an unit")}
                                                            options={alternativeUnitOptions}
                                                            value={alternativeUnitOptions?.find(
                                                                (option: Option) =>
                                                                    option.value ===
                                                                    values?.alternativeUnitGroupInput?.alternativeUnit
                                                            )}
                                                            onChange={(selectedOption: Option) =>
                                                                handleAlternativeUnitChange(
                                                                    selectedOption,
                                                                    setFieldValue
                                                                )
                                                            }
                                                            isSearchable={false}
                                                            disabled={isProductFormReadOnly || isAltNoneSelected}
                                                        />
                                                        <ErrorMessage name="alternativeUnitGroupInput.alternativeUnit">
                                                            {(msg) => (
                                                                <div className="invalid-feedback d-block">{msg}</div>
                                                            )}
                                                        </ErrorMessage>
                                                    </Col>
                                                </Row>
                                            </Col>
                                        </Row>
                                        <Row className="pt-6 mb-4">
                                            <Col>
                                                <Row className="d-flex align-items-center justify-content-between">
                                                    <Col xs={6} md={5}>
                                                        <h5 className="mb-0">
                                                            {t("Additional product attributes")}
                                                            <TooltipCustom
                                                                placement="right"
                                                                text={t(
                                                                    "You can add your own product" +
                                                                        " properties, for example: Color, Tone, Used with, etc."
                                                                )}
                                                            >
                                                                <i className="bi bi-info-circle fs-16 text-dark ms-3"></i>
                                                            </TooltipCustom>
                                                        </h5>
                                                    </Col>
                                                    <Col xs={6} md={2} className="d-flex justify-content-end">
                                                        <TooltipCustom placement="bottom" text={t("Add attribute")}>
                                                            <Button
                                                                variant="outline-secondary"
                                                                className="btn-square px-3 py-6"
                                                                disabled={isProductFormReadOnly}
                                                                onClick={() =>
                                                                    setFieldValue("productAttributes", [
                                                                        ...values.productAttributes,
                                                                        { name: "", property: "" },
                                                                    ])
                                                                }
                                                            >
                                                                <i className="bi bi-plus-lg text-dark"></i>
                                                            </Button>
                                                        </TooltipCustom>
                                                    </Col>
                                                </Row>
                                                {values?.productAttributes?.length > 0 ? (
                                                    <Row className="mt-3">
                                                        <Col>
                                                            <FieldArray
                                                                name="productAttributes"
                                                                render={({ remove }) => (
                                                                    <>
                                                                        {values?.productAttributes?.map(
                                                                            (_: any, index: number) => {
                                                                                return (
                                                                                    <div
                                                                                        key={index}
                                                                                        className="d-flex flex-column mb-3"
                                                                                    >
                                                                                        <Row className="mb-4 fs-7">
                                                                                            <TextInput
                                                                                                id={`updateProduct-productAttributes.${index}.name-field`}
                                                                                                name={`productAttributes.${index}.name`}
                                                                                                placeholder={t(
                                                                                                    "Attributes.Add Attribute"
                                                                                                )}
                                                                                                label={t(
                                                                                                    "Additional Attribute"
                                                                                                )}
                                                                                                required={true}
                                                                                                maxLength={20}
                                                                                                infoText="for ex. color"
                                                                                                disabled={
                                                                                                    isProductFormReadOnly
                                                                                                }
                                                                                            />
                                                                                        </Row>
                                                                                        <TextInputWithButton
                                                                                            id={`updateProduct-productAttributes.${index}.name-field`}
                                                                                            name={`productAttributes.${index}.property`}
                                                                                            index={index}
                                                                                            remove={remove}
                                                                                            label={t("Property")}
                                                                                            infoText="for ex. blue"
                                                                                            disabled={
                                                                                                isProductFormReadOnly
                                                                                            }
                                                                                        />
                                                                                    </div>
                                                                                );
                                                                            }
                                                                        )}
                                                                    </>
                                                                )}
                                                            />
                                                        </Col>
                                                    </Row>
                                                ) : null}
                                                <Row className="mt-4 pt-6">
                                                    <Col>
                                                        <h5 className="text-dark mb-4">{t("Technological Maps")}</h5>
                                                        <TechnologicalMapsList
                                                            technologicalMaps={
                                                                product?.technologicalMapProductRelations
                                                            }
                                                        />
                                                    </Col>
                                                </Row>
                                            </Col>
                                        </Row>
                                    </Col>
                                    <Col lg={4} xxl={3}>
                                        <div className="d-flex w-100 flex-column  justify-content-lg-end">
                                            <h4 className="mb-3 w-100">{t("Product's photo")}</h4>
                                            <ImageCrop
                                                previewPhoto={product?.image}
                                                setFieldValue={setFieldValue}
                                                fieldName="logo"
                                                modalTitle={t("Product's photo")}
                                                notBGinfo={true}
                                                fieldSetter={() => handlePhoto(setFieldValue)}
                                                dismissImage={() => {
                                                    setFieldValue("isUpdateLogo", true);
                                                    setFieldValue("logo", null);
                                                }}
                                                disabled={isProductFormReadOnly}
                                            />
                                            <ErrorMessage name="logo">{(msg) => <div>{msg}</div>}</ErrorMessage>
                                        </div>
                                    </Col>
                                </Row>
                                <div className="d-sm-flex my-4 mt-lg-0">
                                    <Button
                                        type="button"
                                        variant="outline-primary"
                                        className="custom-btn mb-3 mb-sm-0 me-3"
                                        onClick={handleCancel}
                                    >
                                        {t("cancel")}
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        className="custom-btn fw-bold"
                                        disabled={isSubmitting || !canSubmit}
                                    >
                                        {isSubmitting ? (
                                            <SpinnerLoader
                                                variant="light"
                                                loadingText={isEdit ? t("Editing...") : t("Restoring...")}
                                            />
                                        ) : isEdit ? (
                                            t("Save")
                                        ) : (
                                            t("Restore")
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        </>
                    );
                }}
            </Formik>
            {isProductModalOpen && productCategoriesData && !productCategoryLoading ? (
                <ServiceModalWrapper
                    companyId={Number(currentCompanyId)}
                    branchId={Number(branchId)}
                    i18n={i18next}
                    selectedCategoryId={startValueCategory}
                    selectedSubcategoryId={startValueSubCategory}
                    apolloUrl={process.env.REACT_APP_API_URL}
                    isOpen={isProductModalOpen}
                    onClose={() => {
                        setIsProductModalOpen(!isProductModalOpen);
                    }}
                    onCategorySelect={(selectedCategory) => {
                        handleCategoryNameChange(selectedCategory?.name);
                        setPickedCategoryId(selectedCategory?.id);
                        setIsArchiveCategory(selectedCategory?.deleted);
                        setIsProductModalOpen(!isProductModalOpen);
                    }}
                    onSubcategorySelect={(selectedSubcategory) => {
                        handleSubCategoryNameChange(
                            selectedSubcategory?.name,
                            selectedSubcategory?.parentCategory?.name
                        );
                        setPickedCategoryId(selectedSubcategory?.id);
                        setIsArchiveCategory(selectedSubcategory?.deleted);
                        setIsProductModalOpen(!isProductModalOpen);
                    }}
                    categoryList={categoryList}
                    categoryInput={categoryInput}
                    categoryListData={categoryListData}
                    refetchCategories={refetchCategories}
                    onSubmitCreate={handleSubmitCreateCategory}
                    canCreateCategory={canCreateProductCategory}
                    currentCategory={currentCategory}
                    categoryService={CategoryProduct}
                    requestInputService={RequestInputService}
                    normalizeCategoryData={normalizeCategoryData}
                />
            ) : null}
            <CustomModal
                show={isDuplicateProduct}
                handleClose={handleCloseDuplicateOfProductModal}
                title={
                    <Container>
                        <Row>
                            <h5 className="m-0 p-0 fw-normal">{t("The product already exists")}</h5>
                        </Row>
                    </Container>
                }
                body={
                    <Container>
                        <Row>
                            <Col>
                                <span className="fs-16 me-1">
                                    {t(
                                        "Such a product already exists, you can" +
                                            " familiarize yourself with it at the link"
                                    )}
                                </span>
                                <div>
                                    <Button
                                        variant="link"
                                        className="fs-normal text-primary text-decoration-underline cursor-pointer"
                                        onClick={handleNavigateAndClose}
                                    >
                                        {`${duplicateProductData?.brand} ${duplicateProductData?.name}`}
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                }
                buttons={[
                    {
                        text: t("ProductDuplicate.Back"),
                        onClick: handleCloseDuplicateOfProductModal,
                        variant: "outline-primary",
                        className: "w-100 fw-normal",
                    },
                ]}
            />
            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </>
    );
};

export default UpdateRecoveryProductForm;
